import type { PaginatedResponse } from '@/shared/types/api.types';

export interface Pro20PageLike<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

function isPro20PageLike(value: unknown): value is Pro20PageLike<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'content' in value &&
    Array.isArray((value as Pro20PageLike<unknown>).content)
  );
}

/**
 * Maps PRO20 Spring-style page (or bare array) into UI PaginatedResponse.
 * UI pages are 1-based; PRO20 `number` is 0-based.
 */
export function normalizePro20PageResponse<T>(
  results: unknown,
  mapItem: (item: unknown) => T | null,
  fallbackSize = 20,
): PaginatedResponse<T> {
  const mapItems = (items: unknown[]): T[] =>
    items
      .map((item) => mapItem(item))
      .filter((item): item is T => item !== null);

  if (isPro20PageLike(results)) {
    return {
      data: mapItems(results.content),
      meta: {
        page: results.number + 1,
        limit: results.size,
        total: results.totalElements,
        totalPages: Math.max(results.totalPages, 1),
      },
    };
  }

  if (Array.isArray(results)) {
    const data = mapItems(results);
    return {
      data,
      meta: {
        page: 1,
        limit: data.length || fallbackSize,
        total: data.length,
        totalPages: 1,
      },
    };
  }

  const single = mapItem(results);
  if (single) {
    return {
      data: [single],
      meta: {
        page: 1,
        limit: fallbackSize,
        total: 1,
        totalPages: 1,
      },
    };
  }

  return {
    data: [],
    meta: {
      page: 1,
      limit: fallbackSize,
      total: 0,
      totalPages: 1,
    },
  };
}

export interface BranchListQueryParams {
  branchCode: string;
  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  search?: string;
}

/** Large page size for select/dropdown consumers that need the full branch list. */
export const BRANCH_LIST_SELECT_SIZE = 500;

export function buildBranchListQueryString({
  page,
  size,
  sortBy,
  sortDirection,
  search,
}: Omit<BranchListQueryParams, 'branchCode'>): string {
  const params = new URLSearchParams();

  if (page !== undefined) {
    params.set('page', String(page));
  }
  if (size !== undefined) {
    params.set('size', String(size));
  }
  if (sortBy) {
    params.set('sortBy', sortBy);
  }
  if (sortDirection) {
    params.set('sortDirection', sortDirection);
  }
  if (search?.trim()) {
    params.set('search', search.trim());
  }

  const query = params.toString();
  return query ? `?${query}` : '';
}
